import React, { FC, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faRefresh } from "@fortawesome/pro-solid-svg-icons/faRefresh";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { differenceInHours } from "date-fns/esm";
import { defineMessages, useIntl } from "react-intl";

import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { Button } from "@mapmycustomers/ui";

import { isRefreshing } from "@app/store/pinLegends";
import { refreshPinLegend } from "@app/store/pinLegends/actions";
import { RootState } from "@app/store/rootReducer";
import { formatRawDateRelative } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import styles from "./RefreshPinLegend.module.scss";

const messages = defineMessages({
  action: {
    id: "map.layer.key.legend.refreshing.action",
    defaultMessage: "Refresh {icon}",
    description: "A refresh button title on the pin legend's refresh component",
  },
  never: {
    id: "map.layer.key.legend.refreshing.date.never",
    defaultMessage: "never",
    description: "A date to display when the pin legend has no stats data",
  },
  title: {
    id: "map.layer.key.legend.refreshing.title",
    defaultMessage: "Last Updated: {date}",
    description: "A title to display on the pin legend's refresh component",
  },
});

export interface RefreshPinLegendProps {
  pinLegend?: PinLegend;
}

interface Props extends RefreshPinLegendProps {
  onRefreshPinLegend?: typeof refreshPinLegend.request;
  refreshing?: boolean;
}

const RefreshPinLegend: FC<Props> = ({ onRefreshPinLegend, pinLegend, refreshing }) => {
  const intl = useIntl();

  const handleRefreshClick = useCallback(() => {
    if (pinLegend && onRefreshPinLegend) {
      onRefreshPinLegend({ id: pinLegend.id });
    }
  }, [onRefreshPinLegend, pinLegend]);

  useEffect(() => {
    // automatically fetch stats if it's not yet present or are older than a day
    if (
      pinLegend &&
      (!pinLegend.stats ||
        differenceInHours(Date.now(), parseApiDate(pinLegend.stats.cacheComputedAt)) >= 24) &&
      onRefreshPinLegend
    ) {
      onRefreshPinLegend({ id: pinLegend.id });
    }
  }, [pinLegend, onRefreshPinLegend]);

  return (
    <div className={styles.container}>
      <div>
        {intl.formatMessage(messages.title, {
          date: formatRawDateRelative(
            pinLegend?.stats?.cacheComputedAt,
            Date.now(),
            intl.formatMessage(messages.never),
            "Pp",
            parseApiDate,
            { useTime: true }
          ),
        })}
      </div>
      <Button className={styles.refreshButton} onClick={handleRefreshClick} type="link">
        {intl.formatMessage(messages.action, {
          icon: <FontAwesomeIcon icon={faRefresh} spin={refreshing} />,
        })}
      </Button>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  refreshing: isRefreshing(state),
});

const mapDispatchToProps = {
  onRefreshPinLegend: refreshPinLegend.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RefreshPinLegend);
